import { AddBox, ManageAccounts, Person } from '@mui/icons-material';
import { Box, Button, Checkbox, Divider, FormControlLabel, Grid, Tooltip } from '@mui/material';
import { t } from 'i18next';
import { useEffect, useMemo, useRef, useState } from 'react';
import { GenericDialog } from 'src/shared/components/organisms/genericDialog';
import ConsoleHelper from 'src/shared/functions/consoleHelper';
import { useLocalStorage } from 'src/shared/hooks/useLocalStorage';
import { LocalStorageKeys } from 'src/shared/models/enums/localStorageKeys.enum';
import { FormProvider, useForm } from 'react-hook-form';
import { GridCellParams, GridColDef } from '@mui/x-data-grid';
import ControlledTextField from 'src/shared/components/form/controlledTextField';
import { Member } from '../models/member';
import getDefaultMemberSearchFormState, { MemberSearchFormState } from '../models/memberSearchFormState';
import { MemberDataGrid } from './memberDataGrid';
import { MemberAdvancedSearchStatuses } from './memberAdvancedSearchStatuses';
import { MemberStatuses } from './memberStatuses';
import { ControlledMultiDistrictPicker } from '../../../shared/components/form/controlledMultiDistrictPicker';

type MemberSimpleSearchModalProps = {
  modalOpen: boolean;
  onClose: () => void;
  onSelectMember: (memberId: number, member?: Member, createUser?: boolean) => void;
  // Visar en kryssruta för att även skapa användarkonto åt den valda medlemmen (styrelse-flödet).
  allowCreateUser?: boolean;
};

export const MemberSimpleSearchModal = (props: MemberSimpleSearchModalProps) => {
  const { modalOpen, onClose, onSelectMember, allowCreateUser } = props;

  const [searchFilterInitialized, setSearchFilterInitialized] = useState<boolean>(false);
  const [createUser, setCreateUser] = useState<boolean>(false);

  const initialState = getDefaultMemberSearchFormState() as MemberSearchFormState;
  const [advancedSearchFilter, setAdvancedSearchFilter] = useLocalStorage<MemberSearchFormState>(LocalStorageKeys.MemberAdvancedSearchFilter, {
    ...initialState,
    associationIds: [],
    districtIds: [],
    searchString: '',
    postCode: '',
  } as unknown as MemberSearchFormState);

  const formMethods = useForm<MemberSearchFormState>({
    defaultValues: {
      ...initialState,
      associationIds: [],
      districtIds: [],
      searchString: '',
      postCode: '',
      status: [],
    },
  });

  const { handleSubmit, reset } = formMethods;

  const formRef = useRef<HTMLFormElement>(null);

  const searchFilter: MemberSearchFormState = useMemo(() => {
    return {
      pageSize: advancedSearchFilter?.pageSize,
      pageIndex: advancedSearchFilter?.pageIndex,
      sortCol: advancedSearchFilter?.sortCol,
      sortDir: advancedSearchFilter?.sortDir,
      associationIds: advancedSearchFilter?.associationIds || [],
      districtIds: advancedSearchFilter?.districtIds || [],
      searchString: advancedSearchFilter?.searchString,
      status: advancedSearchFilter?.status || [],
      postCode: advancedSearchFilter?.postCode || '',
    } as unknown as MemberSearchFormState;
  }, [
    advancedSearchFilter?.pageSize,
    advancedSearchFilter?.pageIndex,
    advancedSearchFilter?.sortCol,
    advancedSearchFilter?.sortDir,
    advancedSearchFilter?.associationIds,
    advancedSearchFilter?.districtIds,
    advancedSearchFilter?.searchString,
    advancedSearchFilter?.status,
    advancedSearchFilter?.postCode,
  ]);

  const onSubmit = (data, e) => {
    e.preventDefault();
    setAdvancedSearchFilter(data);
  };

  useEffect(() => {
    if (!searchFilterInitialized) {
      const filter: MemberSearchFormState = {
        ...initialState,
      };
      Object.keys(advancedSearchFilter).forEach((key) => {
        const v = advancedSearchFilter[key];
        if (v && v !== null) {
          ConsoleHelper.log('reset', key, v);
          filter[key] = v;
        }
      });
      ConsoleHelper.log('reset', filter);
      reset(filter);
      setSearchFilterInitialized(true);
    }
  }, [advancedSearchFilter, initialState, reset, searchFilterInitialized]);

  return (
    <GenericDialog
      open={modalOpen}
      onClose={() => onClose()}
      onSave={() => ConsoleHelper.log('save')}
      dialogTitle={t('member:memberAdvancedSearch.selectMemberHeader')}
      padding={0}
      dialogContent={
        <>
          <FormProvider {...formMethods}>
            <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
              <Box sx={{ p: 2 }}>
                <Grid container spacing={2}>
                  <Grid item xs={4}>
                    {searchFilterInitialized && <ControlledTextField name='searchString' label={t('member:memberAdvancedSearch.searchString')} />}
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledTextField name='postCode' label={t('member:memberAdvancedSearch.postCode')} />
                  </Grid>
                  <Grid item xs={4}>
                    <ControlledMultiDistrictPicker name='districtIds' />
                  </Grid>
                  <Grid item xs={12}>
                    <MemberAdvancedSearchStatuses otherContactMode={false} />
                  </Grid>
                  <Grid item xs={12}>
                    <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
                      <Button type='submit' variant='contained' disableElevation size='medium'>
                        {t('common:button.search')}
                      </Button>
                    </Box>
                  </Grid>
                </Grid>
              </Box>
            </form>
          </FormProvider>
          <Divider />
          {searchFilter && (
            <MemberDataGrid
              otherContactMode={false}
              searchFilter={searchFilter}
              searchFilterCallback={(newFilter) =>
                setAdvancedSearchFilter((prev) => ({
                  ...prev,
                  ...newFilter,
                }))
              }
              disableRowClick
              gridColumns={
                [
                  { field: 'memberNr', headerName: t('member:tableHeaders.member_id'), flex: 0.5 },
                  {
                    field: 'firstName',
                    headerName: t('member:tableHeaders.name'),
                    sortable: true,
                    flex: 0.75,
                    renderCell: (params: GridCellParams) => {
                      const { isFamilyMember, isMainMember } = params.row;
                      return (
                        <Box sx={{ display: 'flex', width: '100%', justifyContent: 'space-between' }}>
                          <span>
                            {params.row.firstName || ''} {params.row.lastName || ''}
                          </span>
                          {isFamilyMember && !isMainMember && (
                            <Tooltip title={t('member:memberDetails.familyMember')} arrow>
                              <Person fontSize='small' />
                            </Tooltip>
                          )}
                          {isMainMember && (
                            <Tooltip title={t('member:memberDetails.headMember')} arrow>
                              <ManageAccounts fontSize='small' />
                            </Tooltip>
                          )}
                        </Box>
                      );
                    },
                  },
                  {
                    field: 'member_id',
                    sortable: false,
                    flex: 1, // 140
                    headerName: t('member:tableHeaders.ort'),
                    renderCell: (params: GridCellParams) => {
                      const { postAddress } = params.row;
                      return <>{postAddress}</>;
                    },
                  },
                  {
                    field: 'status',
                    headerName: t('member:tableHeaders.memberStatus'),
                    sortable: false,
                    flex: 0.5,
                    renderCell: (params: GridCellParams) => {
                      const { status: stat, resDate } = params.row;
                      return <MemberStatuses status={stat} resDate={resDate} chipSize='small' />;
                    },
                  },
                  {
                    field: 'id',
                    sortable: false,
                    flex: 0.5, // 140
                    headerName: '',
                    renderCell: (params: GridCellParams) => {
                      return (
                        <Button
                          size='small'
                          disableElevation
                          startIcon={<AddBox fontSize='small' />}
                          onClick={() => onSelectMember(params.row.member_id, params.row as Member, allowCreateUser ? createUser : false)}
                        >
                          {t('common:button.choose')}
                        </Button>
                      );
                    },
                  },
                ] as GridColDef[]
              }
              loadingCallback={() => ConsoleHelper.log('')}
              checkboxSelection={false}
            />
          )}
        </>
      }
      dialogActions={
        <Box display='flex' flexDirection='row' justifyContent='space-between' alignItems='center' width='100%'>
          {allowCreateUser ? (
            <FormControlLabel
              control={<Checkbox checked={createUser} onChange={(e) => setCreateUser(e.target.checked)} />}
              label={t('member:memberAdvancedSearch.createUserForMember')}
            />
          ) : (
            <span />
          )}
          <Button variant='outlined' onClick={() => onClose()}>
            {t('common:button.cancel')}
          </Button>
        </Box>
      }
    />
  );
};
